// src/components/routing/refusalCopy.js
//
// One message per refusal. Each says WHAT HAPPENED and WHAT THE PERSON CAN DO.
//
// ── THE CROSS-COMPONENT REFUSAL IS NOT AN ERROR ─────────────────────────────
//
// It is the most common refusal on this corpus and the easiest to word badly.
// The road data resolves into twelve large components whose hulls do not
// overlap in any of the sixty-six pairs — median separation 95.65 km — so two
// points with no connected route between them is the NORMAL state of a pair
// picked at random, not a malfunction. 89.7% of the distance needed to join
// those components lies over ground where no road was extracted at all.
//
// So the copy names the gap and says the data does not contain a connection.
// It does not apologise, does not say "failed", and does not suggest retrying —
// retrying is precisely what will not help.

import { REFUSAL } from "../../stores/useRoutingStore";

const MI = 1609.344;

export function refusalCopy(error, body) {
  const b = body || error?.body || {};

  switch (error?.kind) {
    case REFUSAL.DISCONNECTED: {
      const a = b.from_component, c = b.to_component;
      const sizes = a && c
        ? `The road near you is part of a ${a.edge_count.toLocaleString()}-segment network; the road at your destination is part of a separate ${c.edge_count.toLocaleString()}-segment one.`
        : "Each point sits on a separate stretch of road.";
      return {
        tone: "neutral",
        title: "No connected road between these points",
        body: `${sizes} The imagery these roads were traced from does not cover the ground between them, so there is no route to show.`,
        action: "Pick a destination on the same stretch of road, or travel between the two areas by a route you already know.",
      };
    }

    case REFUSAL.SNAP_TOO_FAR: {
      const which = b.endpoint === "to" ? "destination" : "starting point";
      const miles = Number.isFinite(b.distance_m) ? (b.distance_m / MI).toFixed(1) : null;
      return {
        tone: "neutral",
        title: `No road near the ${which}`,
        body: miles
          ? `The nearest extracted road is ${miles} mi away. Roads are only mapped where imagery was flown.`
          : "The nearest extracted road is too far away to start from.",
        action: `Move the ${which} closer to a road you can see on the map.`,
      };
    }

    case REFUSAL.NO_PATH:
      return {
        tone: "neutral",
        title: "No route found",
        body: "Both points sit on the same road network, but no path through it connects them.",
        action: "Try a destination a little further along the road.",
      };

    case REFUSAL.NOT_ENTITLED:
      return {
        tone: "blocked",
        title: "Roads are not included in your subscription",
        body: "Routing uses the linear infrastructure layer, which this organization is not licensed for.",
        action: "Contact your administrator to have it added.",
      };

    case REFUSAL.PAD_NOT_FOUND:
      return {
        tone: "blocked",
        title: "That pad is no longer available",
        body: "The selected pad is not in the current data — it may have been superseded since this screen was opened.",
        action: "Tap a pad on the map to pick it again.",
      };

    case REFUSAL.NO_BUILD:
      return {
        tone: "blocked",
        title: "Road network unavailable",
        body: "No road network has been published yet. This is a server-side state, not something on this device.",
        action: "Try again later, or contact support if it persists.",
      };

    case REFUSAL.UNAUTHORIZED:
      return {
        tone: "blocked",
        title: "Signed out",
        body: "Your session has expired.",
        action: "Sign in again to continue.",
      };

    case REFUSAL.BAD_REQUEST:
      return {
        tone: "blocked",
        title: "That request could not be read",
        body: b.error || "The starting point or destination was not in a form the server accepts.",
        action: "Set the starting point and destination again.",
      };

    case REFUSAL.OFFLINE_STALE:
      return {
        tone: "stale",
        title: "Showing your last route",
        body: "No connection. This is the route you requested earlier, for these exact points — not a new one.",
        action: "It will refresh when the connection returns.",
      };

    case REFUSAL.OFFLINE:
      return {
        tone: "blocked",
        title: "No connection",
        body: "This route has not been loaded before, so there is nothing saved to show for it.",
        action: "Try again when you have signal.",
      };

    default:
      return {
        tone: "blocked",
        title: "Something went wrong",
        body: "The server could not complete the request.",
        action: "Try again.",
      };
  }
}

// Fixed in substance, not in wording: this is not navigation.
export const ADVISORY =
  "Advisory only. Roads are traced from imagery — gates, washouts and access permission are not verified.";
